import { Color3, Color4, PhysicsMotionType, PhysicsShapeType, Vector2, Vector3 } from '@babylonjs/core';
import { Asset, AssetType } from '../Asset';
import { AssetObject } from '../AssetObject';
import { DecorationConfig } from '../DecorationGenerator';
import { EnemyConfig } from '../EnemyGenerator';
import { EnemyPrefabs } from '../EnemyPrefabs';
import { FogConfig } from '../FogConfig';
import { Level } from '../Level';
import {
  CameraConfig,
  CollectibleDistributionConfig,
  GrassConfig,
  LevelConfig,
  MaterialConfig,
  PathConfig,
  PlayerConfig,
  PostProcessConfig,
  SoundConfig,
  WaterConfig,
} from '../LevelConfig';
import { LightConfig } from '../LightConfig';
import { HeightMapConfig, SkyBoxConfig } from '../MeshFactory';
import { ObstacleConfig } from '../ObstacleGenerator';
import { ObstaclePrefabs } from '../ObstaclePrefabs';
import { TextureAtlas } from '../TextureAtlas';
import { LevelTemplate } from './LevelTemplate';

export class TemplateMountains implements LevelTemplate {
  create(): LevelConfig {
    const skyboxConfig: SkyBoxConfig = {
      url: 'assets/skybox/SunsetSky.png',
      rotationAxis: 'y',
      rotationSpeed: 0.000005,
    };
    const fogConfig = new FogConfig();

    const lightConfig = new LightConfig();
    lightConfig.shadowCasterDistance = 80;

    const postProcessConfig: PostProcessConfig = {
      fxaa: false,
      motionBlur: 0,
      //blur: { kernel: 32, strength: 1, x: 1, y: 0 },
      highlights: false,
      sepia: false,
      birds: 20,
      //rain: 1,
      //heavyRain: 1,
      //groundFog: 1,
      //advancedRain: 0.25,
    };

    const materialConfig: MaterialConfig = {
      colorMaterialBaseColor: new Color3(0.5, 0.7, 0.9),
      colorMaterialCount: 0,
      textures: [
        { name: 'lava', url: 'assets/textures/lava_small.png', uScale: 1, vScale: 1 },
        { name: 'grass', url: 'assets/textures/realistic-grass.jpg', uScale: 1, vScale: 1 },
        { name: 'mud', url: 'assets/textures/cartoon1/Dirt.jpeg', uScale: 1, vScale: 1 },
        { name: 'stone', url: 'assets/textures/rock_seamless.jpg', uScale: 1, vScale: 1 },
      ],
      globalTextureNames: [],
      materials: [],
    };

    const pathConfig: PathConfig = {
      start: new Vector3(0, 0, 0),
      destination: new Vector3(0, 0, 500),
      pathPoints: 10,
      segmentsPerCurve: 15,
      pathCurvature: 1.5,
      pathHilliness: 0.5,
      pathScale: 3.0,
      pathMinCurveAngle: 90,
      laneWidth: 3,
      spaceAround: 50,
      pathBoundary: 0,
      predefined: [
        new Vector3(-196.078, -23.783, 391.664),
        new Vector3(-194.496, -23.844, 390.132),
        new Vector3(-192.454, -23.95, 388.164),
        new Vector3(-190.036, -24.084, 385.835),
        new Vector3(-187.325, -24.23, 383.223),
        new Vector3(-184.405, -24.372, 380.405),
        new Vector3(-181.359, -24.493, 377.458),
        new Vector3(-178.269, -24.577, 374.459),
        new Vector3(-175.22, -24.608, 371.485),
        new Vector3(-172.295, -24.57, 368.612),
        new Vector3(-169.576, -24.446, 365.918),
        new Vector3(-166.915, -24.202, 363.369),
        new Vector3(-164.143, -23.833, 360.865),
        new Vector3(-161.312, -23.374, 358.387),
        new Vector3(-158.476, -22.856, 355.914),
        new Vector3(-155.688, -22.313, 353.428),
        new Vector3(-153.001, -21.775, 350.907),
        new Vector3(-150.469, -21.277, 348.333),
        new Vector3(-148.144, -20.85, 345.685),
        new Vector3(-146.08, -20.527, 342.943),
        new Vector3(-144.331, -20.34, 340.088),
        new Vector3(-142.926, -20.291, 337.17),
        new Vector3(-141.828, -20.347, 334.237),
        new Vector3(-140.987, -20.488, 331.266),
        new Vector3(-140.352, -20.696, 328.237),
        new Vector3(-139.875, -20.952, 325.125),
        new Vector3(-139.503, -21.238, 321.908),
        new Vector3(-139.188, -21.533, 318.565),
        new Vector3(-138.879, -21.82, 315.073),
        new Vector3(-138.527, -22.079, 311.409),
        new Vector3(-138.08, -22.292, 307.551),
        new Vector3(-137.568, -22.481, 303.397),
        new Vector3(-137.059, -22.681, 298.915),
        new Vector3(-136.561, -22.885, 294.184),
        new Vector3(-136.083, -23.088, 289.284),
        new Vector3(-135.634, -23.285, 284.297),
        new Vector3(-135.223, -23.471, 279.301),
        new Vector3(-134.858, -23.641, 274.377),
        new Vector3(-134.548, -23.789, 269.606),
        new Vector3(-134.302, -23.911, 265.067),
        new Vector3(-134.129, -24.0, 260.841),
        new Vector3(-134.017, -24.053, 256.894),
        new Vector3(-133.952, -24.072, 253.124),
        new Vector3(-133.935, -24.065, 249.51),
        new Vector3(-133.97, -24.035, 246.028),
        new Vector3(-134.059, -23.99, 242.658),
        new Vector3(-134.204, -23.934, 239.376),
        new Vector3(-134.408, -23.873, 236.16),
        new Vector3(-134.674, -23.813, 232.988),
        new Vector3(-135.003, -23.76, 229.838),
        new Vector3(-135.399, -23.719, 226.688),
        new Vector3(-135.873, -23.689, 223.558),
        new Vector3(-136.431, -23.663, 220.48),
        new Vector3(-137.063, -23.64, 217.456),
        new Vector3(-137.758, -23.616, 214.483),
        new Vector3(-138.508, -23.589, 211.561),
        new Vector3(-139.301, -23.558, 208.689),
        new Vector3(-140.129, -23.521, 205.867),
        new Vector3(-140.982, -23.474, 203.093),
        new Vector3(-141.849, -23.416, 200.366),
        new Vector3(-142.721, -23.346, 197.686),
        new Vector3(-143.635, -23.268, 195.012),
        new Vector3(-144.621, -23.191, 192.32),
        new Vector3(-145.661, -23.11, 189.64),
        new Vector3(-146.735, -23.023, 187.0),
        new Vector3(-147.825, -22.925, 184.426),
        new Vector3(-148.91, -22.812, 181.948),
        new Vector3(-149.971, -22.683, 179.594),
        new Vector3(-150.99, -22.532, 177.391),
        new Vector3(-151.946, -22.357, 175.367),
        new Vector3(-152.822, -22.154, 173.55),
        new Vector3(-153.624, -21.91, 171.971),
        new Vector3(-154.378, -21.623, 170.614),
        new Vector3(-155.089, -21.303, 169.447),
        new Vector3(-155.761, -20.959, 168.433),
        new Vector3(-156.401, -20.602, 167.538),
        new Vector3(-157.013, -20.242, 166.726),
        new Vector3(-157.603, -19.891, 165.964),
        new Vector3(-158.175, -19.556, 165.215),
        new Vector3(-158.735, -19.25, 164.446),
        new Vector3(-159.287, -18.983, 163.62),
        new Vector3(-159.821, -18.736, 162.805),
        new Vector3(-160.323, -18.491, 162.072),
        new Vector3(-160.799, -18.253, 161.399),
        new Vector3(-161.254, -18.027, 160.759),
        new Vector3(-161.694, -17.82, 160.128),
        new Vector3(-162.123, -17.636, 159.481),
        new Vector3(-162.547, -17.482, 158.794),
        new Vector3(-162.972, -17.363, 158.042),
        new Vector3(-163.402, -17.284, 157.201),
        new Vector3(-163.842, -17.252, 156.245),
        new Vector3(-164.299, -17.27, 155.244),
        new Vector3(-164.768, -17.333, 154.264),
        new Vector3(-165.243, -17.437, 153.271),
        new Vector3(-165.719, -17.575, 152.233),
        new Vector3(-166.19, -17.743, 151.115),
        new Vector3(-166.649, -17.936, 149.886),
        new Vector3(-167.091, -18.147, 148.511),
        new Vector3(-167.508, -18.373, 146.959),
        new Vector3(-167.897, -18.606, 145.195),
        new Vector3(-168.249, -18.843, 143.188),
        new Vector3(-168.552, -19.097, 140.892),
        new Vector3(-168.805, -19.383, 138.318),
        new Vector3(-169.019, -19.693, 135.514),
        new Vector3(-169.208, -20.021, 132.526),
        new Vector3(-169.384, -20.36, 129.404),
        new Vector3(-169.558, -20.703, 126.193),
        new Vector3(-169.744, -21.044, 122.943),
        new Vector3(-169.954, -21.375, 119.7),
        new Vector3(-170.201, -21.69, 116.511),
        new Vector3(-170.496, -21.981, 113.426),
        new Vector3(-170.809, -22.262, 110.425),
        new Vector3(-171.11, -22.546, 107.45),
        new Vector3(-171.413, -22.829, 104.486),
        new Vector3(-171.731, -23.105, 101.519),
        new Vector3(-172.078, -23.372, 98.534),
        new Vector3(-172.468, -23.624, 95.515),
        new Vector3(-172.914, -23.858, 92.449),
        new Vector3(-173.43, -24.069, 89.32),
        new Vector3(-174.029, -24.253, 86.114),
        new Vector3(-174.726, -24.405, 82.816),
        new Vector3(-175.557, -24.528, 79.415),
        new Vector3(-176.53, -24.628, 75.92),
        new Vector3(-177.611, -24.706, 72.348),
        new Vector3(-178.769, -24.762, 68.712),
        new Vector3(-179.97, -24.797, 65.03),
        new Vector3(-181.182, -24.811, 61.315),
        new Vector3(-182.373, -24.807, 57.585),
        new Vector3(-183.51, -24.783, 53.853),
        new Vector3(-184.559, -24.742, 50.136),
        new Vector3(-185.49, -24.683, 46.449),
        new Vector3(-186.354, -24.595, 42.859),
        new Vector3(-187.216, -24.47, 39.386),
        new Vector3(-188.058, -24.315, 35.978),
        new Vector3(-188.862, -24.139, 32.582),
        new Vector3(-189.612, -23.948, 29.144),
        new Vector3(-190.288, -23.753, 25.612),
        new Vector3(-190.875, -23.559, 21.932),
        new Vector3(-191.354, -23.375, 18.053),
        new Vector3(-191.708, -23.21, 13.921),
        new Vector3(-191.919, -23.071, 9.483),
        new Vector3(-191.999, -22.952, 4.58),
        new Vector3(-191.973, -22.842, -0.819),
        new Vector3(-191.843, -22.742, -6.581),
        new Vector3(-191.611, -22.649, -12.57),
        new Vector3(-191.278, -22.562, -18.651),
        new Vector3(-190.846, -22.482, -24.69),
        new Vector3(-190.317, -22.408, -30.552),
        new Vector3(-189.693, -22.337, -36.102),
        new Vector3(-188.976, -22.27, -41.204),
        new Vector3(-188.167, -22.206, -45.725),
        new Vector3(-187.251, -22.149, -49.658),
        new Vector3(-186.217, -22.103, -53.14),
        new Vector3(-185.075, -22.065, -56.251),
        new Vector3(-183.834, -22.033, -59.067),
        new Vector3(-182.503, -22.004, -61.669),
        new Vector3(-181.093, -21.975, -64.135),
        new Vector3(-179.611, -21.943, -66.544),
        new Vector3(-178.069, -21.905, -68.973),
        new Vector3(-176.474, -21.859, -71.502),
        new Vector3(-174.836, -21.802, -74.21),
        new Vector3(-173.158, -21.729, -77.021),
        new Vector3(-171.429, -21.641, -79.815),
        new Vector3(-169.643, -21.541, -82.596),
        new Vector3(-167.796, -21.436, -85.369),
        new Vector3(-165.879, -21.329, -88.142),
        new Vector3(-163.888, -21.225, -90.919),
        new Vector3(-161.816, -21.129, -93.706),
        new Vector3(-159.657, -21.045, -96.508),
        new Vector3(-157.405, -20.978, -99.332),
        new Vector3(-155.054, -20.932, -102.183),
        new Vector3(-152.538, -20.914, -105.1),
        new Vector3(-149.827, -20.921, -108.097),
        new Vector3(-146.969, -20.947, -111.145),
        new Vector3(-144.009, -20.986, -114.217),
        new Vector3(-140.996, -21.032, -117.284),
        new Vector3(-137.977, -21.077, -120.317),
        new Vector3(-134.999, -21.116, -123.288),
        new Vector3(-132.109, -21.143, -126.168),
        new Vector3(-129.355, -21.15, -128.929),
        new Vector3(-126.783, -21.131, -131.542),
        new Vector3(-124.374, -21.093, -134.043),
        new Vector3(-122.067, -21.045, -136.483),
        new Vector3(-119.85, -20.986, -138.853),
        new Vector3(-117.71, -20.917, -141.144),
        new Vector3(-115.633, -20.837, -143.348),
        new Vector3(-113.608, -20.746, -145.457),
        new Vector3(-111.621, -20.644, -147.463),
        new Vector3(-109.66, -20.529, -149.356),
        new Vector3(-107.711, -20.403, -151.13),
        new Vector3(-105.761, -20.264, -152.774),
        new Vector3(-103.904, -20.111, -154.28),
        new Vector3(-102.202, -19.942, -155.649),
        new Vector3(-100.603, -19.758, -156.894),
        new Vector3(-99.051, -19.563, -158.025),
        new Vector3(-97.493, -19.357, -159.055),
        new Vector3(-95.875, -19.142, -159.996),
        new Vector3(-94.141, -18.921, -160.859),
        new Vector3(-92.239, -18.694, -161.655),
        new Vector3(-90.114, -18.464, -162.397),
        new Vector3(-87.711, -18.233, -163.095),
        new Vector3(-84.947, -17.99, -163.725),
        new Vector3(-81.83, -17.728, -164.26),
        new Vector3(-78.447, -17.452, -164.71),
        new Vector3(-74.884, -17.168, -165.087),
        new Vector3(-71.229, -16.883, -165.4),
        new Vector3(-67.566, -16.602, -165.662),
        new Vector3(-63.983, -16.331, -165.881),
        new Vector3(-60.567, -16.076, -166.07),
        new Vector3(-57.404, -15.843, -166.239),
        new Vector3(-54.58, -15.637, -166.399),
        new Vector3(-51.998, -15.456, -166.524),
        new Vector3(-49.515, -15.289, -166.59),
        new Vector3(-47.146, -15.137, -166.607),
        new Vector3(-44.91, -15.0, -166.586),
        new Vector3(-42.822, -14.876, -166.538),
        new Vector3(-40.9, -14.765, -166.474),
        new Vector3(-39.16, -14.667, -166.404),
        new Vector3(-37.62, -14.58, -166.339),
        new Vector3(-36.296, -14.504, -166.289),
        new Vector3(-35.205, -14.439, -166.266),
      ],
    };

    const grassConfig: GrassConfig = {
      bladeHeight: 1.2,
      bladeWidth: 0.3,
      density: 600,
      interval: 1,
      zone: { min: -30, max: 30 },
      deadZone: { min: -6, max: 6 },
      windSpeed: 1.0,
      windStrength: 0.4,
      baseColor: new Color3(0.05, 0.2, 0.05),
      tipColor: new Color3(0.3, 0.7, 0.1),
      scale: { min: 0.8, max: 1.6 },
      frustumDepth: 150,
    };

    const soundConfig: SoundConfig = {
      backgroundMusic: 'sunnySkies',
      backgroundMusicVolume: 1.0,
    };

    const cameraConfig: CameraConfig = {
      alignToPath: false,
      distance: 8,
      heightOffset: 1.2,
      smoothing: 40,
      alphaSmoothing: 1,
      collision: true,
      orthographic: false,
      orthographicZoom: 8,
      orthographicAlpha: Math.PI / 10,
      orthographicBeta: Math.PI / 3,
    };

    const collectibleConfig: CollectibleDistributionConfig = {
      offset: new Vector3(0, 1, 0),
      probability: 0.1,
      minPoint: 10,
      mixPatternTypes: false,
      maxPatternSize: 3,
      spawnRange: 60,
      despawnRange: 90,
    };

    const waterConfig: WaterConfig = {
      level: -30,
      depth: 50,
      size: 2000,
      effect: true,
      mesh: true,
      renderTargetSize: new Vector2(256, 256), // reduced from 512 for better performance
      bumpTextureUrl: 'assets/textures/waterbump2.png',
      bumpScale: 10,
      windForce: 1,
      waveHeight: 0.1,
      waveLength: 0.1,
      bumpHeight: 0.1,
      windDirection: new Vector2(1, 1),
      waterColor: new Color3(0, 0, 221 / 255),
      colorBlendFactor: 0.2,
      alpha: 0.75,
      backFaceCulling: true, // water only seen from above → performance gain
    };

    const decorationConfig: DecorationConfig = {
      assets: [
        new Asset('treeA', AssetType.MESH, 'assets/model/forest/', 'Tree_1_A_Color1.gltf'),
        new Asset('treeB', AssetType.MESH, 'assets/model/forest/', 'Tree_1_B_Color1.gltf'),
        new Asset('treeC', AssetType.MESH, 'assets/model/forest/', 'Tree_1_C_Color1.gltf'),
        new Asset('treeD', AssetType.MESH, 'assets/model/forest/', 'Tree_2_A_Color1.gltf'),
        new Asset('treeE', AssetType.MESH, 'assets/model/forest/', 'Tree_2_B_Color1.gltf'),
        new Asset('treeF', AssetType.MESH, 'assets/model/forest/', 'Tree_2_C_Color1.gltf'),
        new Asset('treeG', AssetType.MESH, 'assets/model/forest/', 'Tree_3_A_Color1.gltf'),
        new Asset('treeH', AssetType.MESH, 'assets/model/forest/', 'Tree_3_B_Color1.gltf'),
        new Asset('log', AssetType.MESH, 'assets/model/nature/', 'log.glb'),
        new Asset('rockA', AssetType.MESH, 'assets/model/forest_nature/', 'Rock_3_J_Color1.gltf'),
        new Asset('rockB', AssetType.MESH, 'assets/model/forest_nature/', 'Rock_3_M_Color1.gltf'),
        new Asset('rockC', AssetType.MESH, 'assets/model/forest_nature/', 'Rock_3_N_Color1.gltf'),
        new Asset('flowerYellow', AssetType.MESH, 'assets/model/nature/', 'flower_yellowA.glb'),
        new Asset('flowerRed', AssetType.MESH, 'assets/model/nature/', 'flower_redB.glb'),
        new Asset('flowerPurple', AssetType.MESH, 'assets/model/nature/', 'flower_purpleC.glb'),
        new Asset('cloud', AssetType.MESH, 'assets/model/platformer_cute/', 'cloud.glb'),
      ],
      assetObjects: [
        new AssetObject('treeA').withType('trees').withMetallic(0.1),
        new AssetObject('treeB').withType('trees').withMetallic(0.1),
        new AssetObject('treeC').withType('trees').withMetallic(0.1),
        new AssetObject('treeD').withType('trees').withMetallic(0.1),
        new AssetObject('treeE').withType('trees').withMetallic(0.1),
        new AssetObject('treeF').withType('trees').withMetallic(0.1),
        new AssetObject('treeG').withType('trees').withMetallic(0.1),
        new AssetObject('treeH').withType('trees').withMetallic(0.1),
        new AssetObject('log').withType('trees').withMetallic(0.1),
        new AssetObject('rockA').withType('rocks').withMetallic(0.1),
        new AssetObject('rockB').withType('rocks').withMetallic(0.1),
        new AssetObject('rockC').withType('rocks').withMetallic(0.1),
        new AssetObject('flowerYellow').withType('flowers'),
        new AssetObject('flowerRed').withType('flowers'),
        new AssetObject('flowerPurple').withType('flowers'),
        new AssetObject('cloud').withType('cloud'),
      ],
      distributions: [
        {
          types: ['trees'],
          mode: 'zone',
          zone: { min: -60, max: 60 },
          deadZone: { min: -10, max: 10 },
          density: 1,
          interval: 2,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 1, max: 4 },
          skipProbability: 0,
          skipOccupied: true,
          alignToFace: false,
          useLevelYPosition: true,
          rolling: true,
        },

        {
          types: ['flowers'],
          mode: 'zone',
          zone: { min: -60, max: 60 },
          deadZone: { min: -10, max: 10 },
          density: 6,
          interval: 1,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 2.5, max: 5 },
          skipProbability: 0,
          skipOccupied: true,
          alignToFace: false,
          useLevelYPosition: true,
          rolling: true,
        },

        {
          types: ['cloud'],
          mode: 'zone',
          zone: { min: -500, max: 500 },
          density: 2,
          interval: 2,
          alpha: 0.5,
          offset: new Vector3(0, 40, 0),
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 4, max: 12 },
          skipProbability: 0,
          skipOccupied: false,
        },

        {
          types: ['rocks'],
          mode: 'zone',
          zone: { min: -70, max: 70 },
          deadZone: { min: -10, max: 10 },
          density: 5,
          interval: 1,
          rotationY: { min: 0, max: Math.PI * 2 },
          scale: { min: 0.6, max: 1.2 },
          skipProbability: 0.2,
          useLevelYPosition: true,
          alignToFace: true,
        },
      ],
    };

    const minPoint = 20;

    const obstacleConfig: ObstacleConfig = ObstaclePrefabs.create({
      probabilityMin: 0.01,
      probabilityMid: 0.02,
      probabilityMax: 0.03,
      minPoint: minPoint,
      pathWidth: 10,
      spawnRange: 60,
      despawnRange: 90,
      obstacleColor: 'green',
    });

    const enemyConfig: EnemyConfig = EnemyPrefabs.create({
      probabilityMin: 0.01,
      probabilityMid: 0.02,
      probabilityMax: 0.03,
      minPoint: minPoint,
      pathWidth: 10,
      spawnRange: 60,
      despawnRange: 90,
    });

    // grounds and ceilings matrices should match on their edges, ensure this
    const groundWidth = 1500;
    const groundHeight = 1500;
    const groundSubdivisions = 100;

    const heightMapConfig = HeightMapConfig.builder()
      .name('heightmap-ground')
      .heightMapImageUrl('assets/heightmap/snowdon.png')
      //.heightMapImageUrl('assets/heightmap/ambientocclusion.jpg')
      .width(groundWidth)
      .height(groundHeight)
      .updatable(true)
      .subdivisions(groundSubdivisions)
      .castShadows(false)
      .maxHeight(80)
      .minHeight(0)
      .metadata({
        centerOnPath: false,
      })
      .pathWidth(12) // no corridor
      .pathProjection(true)
      .pathTransitionWidth(7) // Steepness of the walls (greater = gentler)
      .pathYOffset(54) // Optional: Y-offset
      .terrainTextureName1('mud')
      .terrainTextureName2('stone')
      .terrainTextureName3('grass')
      .terrainTextureScale(new Vector3(200, 200, 200))
      .backFaceCulling(false)
      .physicsShapeType(PhysicsShapeType.MESH)
      .physicsMotionType(PhysicsMotionType.ANIMATED)
      .position(new Vector3(0, -55, 0))
      .build();

    const playerConfig: PlayerConfig = {
      startPathIndex: 4,
      startOffset: new Vector3(0, 3, 0),
      autoRun: 0,
      speed: 10,
      jumpSpeed: 11,
      backwardSpeed: 4,
      turnSpeed: 10,
      dashSpeed: 12,
      projectileSpeed: 20,
    };

    const atlasConfig = [new TextureAtlas('cartoon1', 'assets/atlas/', 'cartoon1.png', 'cartoon1.json')];

    const spriteConfig = [
      {
        asset: new Asset('palm_cartoon', AssetType.IMAGE, 'assets/sprites/', 'palm_cartoon.png'),
        scale: { min: 2, max: 7 },
        capacity: 400,
        color: new Color4(1, 1, 1, 1),
        cellWidth: 1088,
        cellHeight: 1200,
        name: 'palm_cartoon',
        offsetY: -1,
        yRange: { min: -5, max: 300 },
      },
      {
        asset: new Asset('tree_cartoon', AssetType.IMAGE, 'assets/sprites/', 'tree_cartoon.png'),
        scale: { min: 2, max: 7 },
        capacity: 400,
        color: new Color4(1, 1, 1, 1),
        cellWidth: 1136,
        cellHeight: 1200,
        name: 'tree_cartoon',
        offsetY: -1,
        yRange: { min: -5, max: 300 },
      },
    ];

    const config = new LevelConfig(
      'Test Level',
      2, // seed
      100, // timelimit
      Level.fullGravity, // gravity
      -45, // killing height
      100, // height above ground
      40, // checkpoint interval
      4, // goal end offset
      playerConfig,
      cameraConfig,
      pathConfig,
      lightConfig,
      skyboxConfig,
      materialConfig
    );

    config.sound = soundConfig;
    //config.fog = fogConfig;
    config.collectible = collectibleConfig;
    config.postProcess = postProcessConfig;
    config.heightMap = heightMapConfig;
    config.decoration = decorationConfig;
    //config.enemy = enemyConfig;
    config.obstacle = obstacleConfig;
    config.grass = grassConfig;
    config.water = waterConfig;
    config.atlasses = atlasConfig;
    //config.sprites = spriteConfig;

    return config;
  }
}
